import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import GeoResponsiveList from "../../common/Components/GeoList/GeoResponsiveList";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { createKarkhanaDesign, createRateCard, listKarkhanaDesigns, listRateCards } from "../../services/karkhanaApi";
import { useKT } from "./i18n/kt";
import "../gi-registry/gi-registry.scss";
import { fmtDay, todayIso, versionHistory } from "./rates/rateModel";
import "./workers/karkhana-workers.scss";
import "./rates/karkhana-rates.scss";
import PageHeader, { HeaderLink, HeaderButton } from "./ui/PageHeader";
import SearchField from "./ui/SearchField";
import { kp } from "./karkhanaPaths";
import { useAddressParam } from "./ui/addressState";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const PAGE = 50;

/** Pick an existing design, or add a new design number on the spot. */
export const DesignPicker = ({
  value,
  onChange,
  excludeIds = [],
  autoFocus = true,
}: {
  value: { id: string; number: string } | null;
  onChange: (d: { id: string; number: string } | null) => void;
  excludeIds?: string[];
  autoFocus?: boolean;
}) => {
  const { t } = useKT();
  const [q, setQ] = useState("");
  const [options, setOptions] = useState<any[]>([]);
  const [adding, setAdding] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  // A string key so a new array each render doesn't refetch forever.
  const excludeKey = excludeIds.join(",");

  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      const skip = excludeKey ? excludeKey.split(",") : [];
      const res: any = await listKarkhanaDesigns({ q: q.trim() || undefined, limit: 20 });
      setOptions(res?.success ? (res.data?.docs || []).filter((d: any) => !skip.includes(d.id)) : []);
    }, 200);
    return () => window.clearTimeout(timer.current);
  }, [q, excludeKey]);

  const exact = options.find((d) => String(d.designNumber).toUpperCase() === q.trim().toUpperCase());

  const addDesign = async () => {
    setAdding(true);
    try {
      const res: any = await createKarkhanaDesign({ designNumber: q.trim() });
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not add the design", type: "danger" });
        return;
      }
      onChange({ id: res.data.id, number: res.data.designNumber });
    } finally {
      setAdding(false);
    }
  };

  if (value) {
    return (
      <div className="kw-toolbar" style={{ margin: 0 }}>
        <span className="kw-chip is-info kw-code">{value.number}</span>
        <Button size="sm" outline onClick={() => onChange(null)}>
          Change
        </Button>
      </div>
    );
  }
  return (
    <>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t("Type a design number, e.g. DN-1497")}
        aria-label="Design number"
        autoFocus={autoFocus}
      />
      <div className="kr-design-options" role="listbox">
        {options.map((d) => (
          <button
            key={d.id}
            type="button"
            className="kr-design-option kw-press"
            onClick={() => onChange({ id: d.id, number: d.designNumber })}
          >
            <span className="kw-code">{d.designNumber}</span>
            {d.designName && d.designName !== d.designNumber ? <span className="kw-muted"> · {d.designName}</span> : null}
          </button>
        ))}
        {q.trim() && !exact ? (
          <button type="button" className="kr-design-option kr-design-option--add kw-press" disabled={adding} onClick={addDesign}>
            {adding ? t("Adding…") : t("Add new design “{{n}}”", { n: q.trim() })}
          </button>
        ) : null}
        {!q.trim() && options.length === 0 ? <p className="kw-muted" style={{ margin: 8 }}>{t("No designs yet. Type a number to add one.")}</p> : null}
      </div>
    </>
  );
};

const KarkhanaRateCardsPage = () => {
  const { t } = useKT();
  const history = useHistory();
  const role = readRole();
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  // Search and page are in the address (?q=…&page=2, replaced): Back from a card returns to the same list.
  const [pageParam, setPageParam] = useAddressParam("page", "1");
  const page = Math.max(0, (parseInt(pageParam, 10) || 1) - 1);
  const setPage = (p: number) => setPageParam(String(p + 1));
  const [query, setQuery] = useAddressParam("q");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [design, setDesign] = useState<{ id: string; number: string } | null>(null);
  const [from, setFrom] = useState(todayIso());
  const [group, setGroup] = useState("");
  const [saving, setSaving] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const load = useCallback(async (q: string, p: number) => {
    setLoading(true);
    setError("");
    try {
      const res: any = await listRateCards({ q: q.trim() || undefined, offset: p, limit: PAGE });
      if (!res?.success) {
        setRows([]);
        setError(res?.message || "Unable to load rate cards");
        return;
      }
      setRows(res.data?.docs || []);
      setTotal(Number(res.data?.length || 0));
    } catch {
      setError("Could not reach the server");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    load(query, page);
  }, [role, history, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (v: string) => {
    setQuery(v);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setPage(0);
      load(v, 0);
    }, 250);
  };

  const create = async () => {
    if (!design) return;
    setSaving(true);
    try {
      const res: any = await createRateCard({ designId: design.id, effectiveFrom: from, rateGroup: group.trim() || undefined, lines: [] });
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not create the rate card", type: "danger" });
        return;
      }
      history.push(kp.rateCard(res.data.id));
    } catch {
      notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  if (!canManageKarkhana(role)) return null;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Rate cards" back={kp.settings()}>
        {canWrite ? <HeaderLink to={kp.rateImport()}>Import from CSV</HeaderLink> : null}
        {canWrite ? (
          <HeaderButton
            primary
            add
            onClick={() => {
              setDesign(null);
              setFrom(todayIso());
              setGroup("");
              setOpen(true);
            }}
          >
            New rate card
          </HeaderButton>
        ) : null}
      </PageHeader>
      <p className="kw-lede">
        One card per design number. Each card keeps every year's rates as a dated version, so old work is always paid
        at the rate that applied then.
      </p>
      <SearchField
        value={query}
        onChange={onSearch}
        placeholder="Search design or group"
        label="Search rate cards"
        count={loading ? "Loading…" : `${total} card${total === 1 ? "" : "s"}`}
      />

      <GeoResponsiveList
        rows={rows}
        rowKey={(r) => r.id}
        empty={loading ? "Loading…" : error || (query ? "No card matches that search" : "No rate cards yet")}
        actions={(r) => (
          <Link className="btn btn-hastkala btn-sm" to={kp.rateCard(r.id)}>
            Open
          </Link>
        )}
        columns={[
          {
            key: "design",
            header: "Design",
            cell: (r) => (
              <Link to={kp.rateCard(r.id)} className="kw-code">
                {r.designNumber}
              </Link>
            ),
          },
          {
            key: "current",
            header: "Rates in use today",
            cell: (r) =>
              r.current ? (
                <span>
                  v{r.current.versionNo} <span className="kw-muted">since {fmtDay(r.current.effectiveFrom)}</span>
                </span>
              ) : (
                <span className="kw-chip is-review">None approved</span>
              ),
          },
          {
            key: "next",
            header: "Versions",
            hideSm: true,
            // Every dated version, oldest first: approved ones with their dates, drafts waiting for the owner.
            cell: (r) => {
              const list = versionHistory<any>(r.versions || []);
              if (!list.length) return "—";
              return (
                <span className="kw-chips">
                  {list.map((v: any) => (
                    <span key={v.id} className={`kw-chip${v.status === "DRAFT" ? " is-review" : v.id === r.current?.id ? " is-ok" : ""}`}>
                      v{v.versionNo} {fmtDay(v.effectiveFrom)}
                      {v.effectiveTo ? ` – ${fmtDay(v.effectiveTo)}` : ""}
                      {v.status === "DRAFT" ? ` · ${t("draft")}` : ""}
                    </span>
                  ))}
                </span>
              );
            },
          },
          { key: "group", header: "Group", hideSm: true, cell: (r) => r.rateGroup || "—" },
          {
            key: "copied",
            header: "Copied from",
            hideSm: true,
            cell: (r) => (r.copiedFromDesignNumber ? <span className="kw-code kw-muted">{r.copiedFromDesignNumber}</span> : "—"),
          },
        ]}
      />
      {pages > 1 ? (
        <div className="kw-toolbar" style={{ marginTop: 12 }}>
          <Button size="sm" outline disabled={page === 0} onClick={() => { setPage(page - 1); load(query, page - 1); }}>
            Previous
          </Button>
          <span className="kw-muted">Page {page + 1} of {pages}</span>
          <Button size="sm" outline disabled={page + 1 >= pages} onClick={() => { setPage(page + 1); load(query, page + 1); }}>
            Next
          </Button>
        </div>
      ) : null}

      <Modal isOpen={open} toggle={() => setOpen(false)} modalClassName="kw-modal">
        <ModalHeader toggle={() => setOpen(false)}>New rate card</ModalHeader>
        <ModalBody>
          <Label>Design</Label>
          <DesignPicker value={design} onChange={setDesign} />
          <Label className="mt-3" for="kr-from">
            Rates apply from
          </Label>
          <Input id="kr-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <Label className="mt-2" for="kr-group">
            Group (optional)
          </Label>
          <Input
            id="kr-group"
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            placeholder="e.g. Silk tanchui"
          />
          <p className="kw-muted mt-2">You'll enter the rates on the next screen. They stay a draft until the admin approves them.</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={!design || !from || saving} onClick={create}>
            {saving ? "Creating…" : "Create and add rates"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaRateCardsPage;
